"use client";

import { revalidatePathAction } from "@/app/actions";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { useSessionContext } from "@/contexts/session-context";
import { useBillingSubscription } from "@/hooks/use-billing";
import { useMembers } from "@/hooks/use-members-info";
import { usePermissions } from "@/hooks/use-permissions";
import { backendClient } from "@/lib/backend-api";
import { ShopSummary } from "@/types/shop";
import { cn } from "@/utils/utils";
import { safe } from "@orpc/client";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import {
  ArrowUpRight,
  Check,
  ChevronsUpDown,
  Settings,
  UserPlus,
} from "lucide-react";
import Link from "next/link";
import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  hardNavigateIfAllowed,
  isEditorLeaveCancelled,
} from "@/utils/editor-unsaved-guard";
import { getPostShopSwitchPath } from "@/utils/post-shop-switch-path";
import { getAvatarFallbackColor } from "@jurni/storefront-ui/shared";
import { updateActiveShopSession } from "@/utils/update-active-shop-session";

interface StoreSwitcherProps {
  currentShop?: ShopSummary;
}

type StoreItem = {
  id: number;
  name: string;
  domain?: string | null;
  favicon?: string | null;
  logo?: string | null;
};

const shopCardShadow =
  "shadow-[inset_0px_1px_0px_0px_#fff,0px_0px_0px_1px_rgba(37,37,39,0.1),0px_1px_2px_-1px_rgba(37,37,39,0.08),0px_2px_4px_0px_rgba(37,37,39,0.04)]";

function StoreAvatar({
  shop,
  size = "sm",
}: {
  shop: StoreItem;
  size?: "sm" | "md" | "lg";
}) {
  const src = shop.favicon || shop.logo || undefined;

  const sizeClass =
    size === "lg" ? "size-10" : size === "md" ? "size-8" : "size-7";
  const textClass = size === "lg" ? "text-sm" : "text-xs";

  return (
    <Avatar className={cn("shrink-0 overflow-hidden rounded-md", sizeClass)}>
      {src && (
        <AvatarImage
          src={src}
          alt={shop.name}
          className="rounded-none object-contain"
        />
      )}
      <AvatarFallback
        className={cn(
          "rounded-none font-medium text-white",
          textClass,
          !src && getAvatarFallbackColor(shop.name),
        )}
      >
        {shop.name.charAt(0).toUpperCase()}
      </AvatarFallback>
    </Avatar>
  );
}

export function StoreSwitcher({ currentShop }: StoreSwitcherProps) {
  const { canSwitchShop } = useSessionContext();
  const { allowed: canInviteMembers } = usePermissions({
    invitation: ["create"],
  });
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  const { data: userShopsData, isLoading: isLoadingUserShops } = useQuery({
    queryKey: ["user-shops"],
    queryFn: async () => {
      const result = await safe(backendClient.shop.listUserShops());
      if (result.error) {
        throw result.error;
      }
      return result.data;
    },
    enabled: !canSwitchShop,
  });

  const { data: allShopsData, isLoading: isLoadingAllShops } = useQuery({
    queryKey: ["switchable-shops", search],
    queryFn: async () => {
      const result = await safe(
        backendClient.session.listSwitchableShops({
          limit: 100,
          search: search.trim() || undefined,
        }),
      );
      if (result.error) {
        throw result.error;
      }
      return result.data;
    },
    enabled: canSwitchShop,
    placeholderData: keepPreviousData,
  });

  const { data: subscriptionResult } = useBillingSubscription();
  const { data: membersResult } = useMembers();

  const { mutate: updateSession } = useMutation({
    mutationFn: (shopId: number) => updateActiveShopSession(shopId),
    onSuccess: () => {
      revalidatePathAction("/");
      toast.success("Store switched successfully");
      // Avoid reloading entity URLs (e.g. /editor/53) that won't exist in the new shop
      window.location.assign(getPostShopSwitchPath(window.location.pathname));
    },
    onError: (error) => {
      if (isEditorLeaveCancelled(error)) return;
      toast.error("Failed to switch store");
      console.error("Store switch error:", error);
    },
  });

  const currentShopId = currentShop?.id;

  const stores: StoreItem[] = useMemo(() => {
    const list = canSwitchShop
      ? (allShopsData?.shops ?? [])
      : (userShopsData?.shops ?? []);

    if (currentShopId == null) return list;

    const currentIndex = list.findIndex((shop) => shop.id === currentShopId);
    if (currentIndex <= 0) return list;

    const sorted = [...list];
    const [current] = sorted.splice(currentIndex, 1);
    sorted.unshift(current);
    return sorted;
  }, [canSwitchShop, allShopsData?.shops, userShopsData?.shops, currentShopId]);

  const currentShopName = currentShop?.name || "Select store";
  const showStoreList = canSwitchShop || stores.length > 1;
  const isLoading = canSwitchShop ? isLoadingAllShops : isLoadingUserShops;

  const planName =
    subscriptionResult?.data?.plan?.name ??
    (subscriptionResult && !subscriptionResult.error ? "Free Plan" : null);

  const memberCount = useMemo(() => {
    const members = membersResult?.data?.members;
    if (!members) return null;
    return members.filter((member) => member.type !== "pending").length;
  }, [membersResult?.data?.members]);

  const metaLabel = useMemo(() => {
    const parts: string[] = [];
    if (planName) parts.push(planName);
    if (memberCount != null) {
      parts.push(`${memberCount} member${memberCount === 1 ? "" : "s"}`);
    }
    return parts.length > 0 ? parts.join(" • ") : null;
  }, [planName, memberCount]);

  const cardTriggerClassName = cn(
    "h-auto w-full gap-0 rounded-md bg-white py-[7px] pl-2 pr-3",
    shopCardShadow,
    "hover:!bg-[#fafafa] data-[state=open]:bg-white",
    "focus-visible:ring-0",
  );

  if (isLoading && !currentShop) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton
            size="lg"
            disabled
            className={cardTriggerClassName}
          >
            <div className="flex w-full items-center gap-2">
              <div className="size-8 animate-pulse rounded-md bg-muted" />
              <div className="flex flex-1 flex-col gap-1">
                <div className="h-2.5 w-8 animate-pulse rounded bg-muted" />
                <div className="h-3.5 w-24 animate-pulse rounded bg-muted" />
              </div>
            </div>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <Popover
          open={isOpen}
          onOpenChange={(open) => {
            setIsOpen(open);
            if (!open) setSearch("");
          }}
        >
          <PopoverTrigger asChild>
            <SidebarMenuButton size="lg" className={cardTriggerClassName}>
              <div className="flex w-full items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2">
                  {currentShop && <StoreAvatar shop={currentShop} size="md" />}
                  <div className="flex min-w-0 flex-col text-left leading-4">
                    <span className="text-[11px] text-muted-foreground">
                      Store
                    </span>
                    <span className="truncate text-[13px] font-medium text-foreground">
                      {currentShopName}
                    </span>
                  </div>
                </div>
                <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" />
              </div>
            </SidebarMenuButton>
          </PopoverTrigger>

          <PopoverContent
            className="w-[280px] p-0"
            side="bottom"
            align="start"
            sideOffset={8}
            onOpenAutoFocus={(event) => {
              event.preventDefault();
              searchInputRef.current?.focus();
            }}
          >
            <div className="flex flex-col gap-3 p-3 pb-2">
              <div className="flex items-start gap-3">
                {currentShop && <StoreAvatar shop={currentShop} size="lg" />}
                <div className="min-w-0 flex-1 pt-0.5">
                  <p className="truncate text-sm font-semibold text-foreground">
                    {currentShopName}
                  </p>
                  {metaLabel && (
                    <p className="truncate text-xs text-muted-foreground">
                      {metaLabel}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 gap-1.5 px-2"
                  asChild
                >
                  <Link href="/settings">
                    <Settings className="size-3.5 text-muted-foreground" />
                    Settings
                  </Link>
                </Button>
                {canInviteMembers && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 gap-1.5 px-2"
                    asChild
                  >
                    <Link href="/settings/members">
                      <UserPlus className="size-3.5 text-muted-foreground" />
                      Invite members
                    </Link>
                  </Button>
                )}
              </div>
            </div>

            {showStoreList && (
              <>
                <Separator />
                <Command shouldFilter={!canSwitchShop}>
                  <div className="px-2 pt-1.5 [&_[cmdk-input-wrapper]]:border-b-0 [&_[cmdk-input-wrapper]]:px-0">
                    <p className="px-1 py-1 text-xs font-medium text-muted-foreground">
                      All stores
                    </p>
                    <CommandInput
                      ref={searchInputRef}
                      autoFocus
                      value={search}
                      onValueChange={setSearch}
                      placeholder="Search stores…"
                    />
                  </div>
                  <Separator />

                  <CommandList className="max-h-56 px-2 pb-1.5">
                    <CommandEmpty>No stores found</CommandEmpty>
                    <CommandGroup className="p-0">
                      {stores.map((shop) => {
                        const isActive = shop.id === currentShopId;
                        return (
                          <CommandItem
                            key={shop.id}
                            value={`${shop.name} ${shop.id}`}
                            className="mt-1.5 gap-2 px-1 py-1.5"
                            onSelect={() => {
                              setIsOpen(false);
                              if (!isActive) updateSession(shop.id);
                            }}
                          >
                            <StoreAvatar shop={shop} size="sm" />
                            <span className="min-w-0 flex-1 truncate text-sm">
                              {shop.name}
                            </span>
                            {isActive && (
                              <Check className="size-4 shrink-0 text-foreground" />
                            )}
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  </CommandList>
                </Command>

                {canSwitchShop && (
                  <>
                    <Separator />
                    <div className="p-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-auto w-full justify-start gap-2 px-2 py-1.5"
                        onClick={() => {
                          setIsOpen(false);
                          void hardNavigateIfAllowed("/admin");
                        }}
                      >
                        <ArrowUpRight className="size-4 text-muted-foreground" />
                        Admin
                      </Button>
                    </div>
                  </>
                )}
              </>
            )}
          </PopoverContent>
        </Popover>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
